import * as React from 'react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { IconClose, IconSearch } from '../chrome/icons';

/*
 * Table archetype primitives (docs/design-brief/table-concept.html,
 * decided v5): page head · toolbar (search + one filter control +
 * primary) · dismissable chips · rows with a right date column and a
 * fixed status column · honest footer. ~16 screens instantiate these.
 */

export function PageHead({ title, sub }: { title: string; sub: string }) {
    return (
        <>
            <h1 className="pg-title">{title}</h1>
            <p className="pg-sub">{sub}</p>
        </>
    );
}

const FilterIcon = () => (
    <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        aria-hidden="true"
    >
        <path d="M4 6h16M7 12h10M10 18h4" />
    </svg>
);

/** Outside-click closer shared by the popovers here. */
function useDismiss(open: boolean, onClose: () => void) {
    const ref = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (!open) return;
        const onDown = (e: MouseEvent) => {
            if (!ref.current?.contains(e.target as Node)) onClose();
        };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, [open, onClose]);
    return ref;
}

export function Toolbar({
    searchValue,
    onSearch,
    searchPlaceholder,
    filterCount,
    menu,
    primaryLabel,
    onPrimary,
    inline,
}: {
    searchValue: string;
    onSearch: (value: string) => void;
    searchPlaceholder: string;
    filterCount: number;
    /** popover contents; receives a close() to call after a pick. Omit to hide the filter button. */
    menu?: (close: () => void) => React.ReactNode;
    primaryLabel?: string;
    onPrimary?: () => void;
    /** controls kept in view beside search (e.g. period) */
    inline?: React.ReactNode;
}) {
    const [open, setOpen] = useState(false);
    const ref = useDismiss(open, () => setOpen(false));
    return (
        <div className="toolbar">
            <label className="tb-search">
                <IconSearch />
                <input
                    value={searchValue}
                    placeholder={searchPlaceholder}
                    spellCheck={false}
                    autoComplete="off"
                    onChange={(e) => onSearch(e.target.value)}
                />
                {searchValue !== '' && (
                    <button
                        className="ic"
                        aria-label="Clear search"
                        title="Clear search"
                        onClick={() => onSearch('')}
                    >
                        <IconClose size={12} />
                    </button>
                )}
            </label>
            {inline}
            {menu && (
            <div ref={ref} className="tb-filter" style={{ position: 'relative' }}>
                {/* square while idle; widens to fit the count when active */}
                <button
                    className={
                        filterCount > 0 ? 'tb-btn tb-on' : 'tb-btn tb-icon'
                    }
                    title="Filter & sort"
                    aria-label="Filter and sort"
                    aria-expanded={open}
                    onMouseDown={() => setOpen((v) => !v)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setOpen((v) => !v);
                        }
                    }}
                >
                    <FilterIcon />
                    {filterCount > 0 && filterCount}
                </button>
                {open && (
                    <div
                        className="menu"
                        style={{ top: 32, right: 0, minWidth: 190 }}
                    >
                        {menu(() => setOpen(false))}
                    </div>
                )}
            </div>
            )}
            {primaryLabel && (
                <button className="tb-new" onClick={onPrimary}>
                    {primaryLabel}
                </button>
            )}
        </div>
    );
}

/** Segmented control with a sliding selection thumb. */
export function Segmented<T extends string>({
    label,
    options,
    value,
    onChange,
}: {
    label: string;
    options: Array<{ value: T; label: string }>;
    value: T;
    onChange: (value: T) => void;
}) {
    const wrap = useRef<HTMLDivElement>(null);
    const [thumb, setThumb] = useState<{ left: number; width: number } | null>(
        null,
    );
    useLayoutEffect(() => {
        const measure = () => {
            const on = wrap.current?.querySelector<HTMLElement>(
                'button[aria-pressed="true"]',
            );
            setThumb(on ? { left: on.offsetLeft, width: on.offsetWidth } : null);
        };
        measure();
        /* the control stretches full-width on phones: re-measure on resize */
        const ro = new ResizeObserver(measure);
        if (wrap.current) ro.observe(wrap.current);
        return () => ro.disconnect();
    }, [value, options.length]);
    return (
        <div className="seg" role="group" aria-label={label} ref={wrap}>
            {thumb && (
                <span
                    className="seg-thumb"
                    style={{
                        transform: `translateX(${thumb.left}px)`,
                        width: thumb.width,
                    }}
                />
            )}
            {options.map((o) => (
                <button
                    key={o.value}
                    type="button"
                    aria-pressed={o.value === value}
                    className={o.value === value ? 'on' : ''}
                    onClick={() => onChange(o.value)}
                >
                    {o.label}
                </button>
            ))}
        </div>
    );
}

export const MenuLabel = ({ children }: { children: React.ReactNode }) => (
    <div className="mlab">{children}</div>
);
export const MenuSep = () => <div className="msep" />;
export function MenuItem({
    on,
    onClick,
    children,
}: {
    on?: boolean;
    onClick: () => void;
    children: React.ReactNode;
}) {
    return (
        <button
            className={on ? 'menu-item on' : 'menu-item'}
            onClick={onClick}
        >
            {children}
            {on && <span className="ck">✓</span>}
        </button>
    );
}

export interface Chip {
    label: string;
    value: string;
    onClear: () => void;
}

export function Chipbar({ chips }: { chips: Chip[] }) {
    if (chips.length === 0) return null;
    return (
        <div className="chipbar">
            {chips.map((chip) => (
                <span className="fchip" key={`${chip.label}-${chip.value}`}>
                    {chip.label} · <b>{chip.value}</b>
                    <button
                        className="x"
                        aria-label={`Clear ${chip.label} filter`}
                        title={`Clear ${chip.label} filter`}
                        onClick={chip.onClear}
                    >
                        <IconClose size={10} />
                    </button>
                </span>
            ))}
        </div>
    );
}

export interface RowMenuItem {
    label: string;
    onClick: () => void;
}

export function TRow({
    name,
    badge,
    meta,
    metaDate,
    date,
    status,
    onOpen,
    href,
    menuItems,
    index,
}: {
    name: string;
    badge?: string;
    /** venue-only on desktop; the date leads it on mobile via metaDate */
    meta: string;
    metaDate?: string;
    date: string;
    status: React.ReactNode;
    onOpen: () => void;
    href?: string;
    menuItems: RowMenuItem[];
    /** position in the list, for the settle stagger */
    index?: number;
}) {
    const [open, setOpen] = useState(false);
    const ref = useDismiss(open, () => setOpen(false));
    return (
        <div
            className="trow"
            onClick={onOpen}
            role="link"
            tabIndex={0}
            style={{ '--i': Math.min(index ?? 0, 10) } as React.CSSProperties}
            onKeyDown={(e) => {
                if (e.key === 'Enter') onOpen();
            }}
        >
            <span className="main">
                <span className="r1">
                    <a
                        className="nm"
                        href={href}
                        onClick={(e) => {
                            /* real link for ⌘-click; plain click uses the row */
                            if (!e.metaKey && !e.ctrlKey) e.preventDefault();
                            e.stopPropagation();
                            if (!e.metaKey && !e.ctrlKey) onOpen();
                        }}
                        style={{ color: 'inherit', textDecoration: 'none' }}
                    >
                        {name}
                        {badge && <span className="badge">{badge}</span>}
                    </a>
                    <span className="stm">{status}</span>
                </span>
                <span className="sub">
                    {metaDate && <span className="subdate">{metaDate} · </span>}
                    {meta}
                </span>
            </span>
            <span className="dt">{date}</span>
            <span className="st">{status}</span>
            {menuItems.length === 0 ? (
                <span style={{ width: 24, flex: 'none' }} />
            ) : (
            <div
                ref={ref}
                style={{ position: 'relative' }}
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    className={open ? 'dots on' : 'dots'}
                    aria-label={`Actions for ${name}`}
                    title={`Actions for ${name}`}
                    aria-expanded={open}
                    onMouseDown={() => setOpen((v) => !v)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setOpen((v) => !v);
                        }
                    }}
                >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                        <circle cx="5" cy="12" r="1.6" />
                        <circle cx="12" cy="12" r="1.6" />
                        <circle cx="19" cy="12" r="1.6" />
                    </svg>
                </button>
                {open && (
                    <div className="menu" style={{ top: 26, right: 0 }}>
                        {menuItems.map((item) => (
                            <button
                                key={item.label}
                                className="menu-item"
                                onClick={() => {
                                    setOpen(false);
                                    item.onClick();
                                }}
                            >
                                {item.label}
                            </button>
                        ))}
                    </div>
                )}
            </div>
            )}
        </div>
    );
}

/** The kit card that holds the rows (kit §5: elevated, border-tertiary,
    soft shadow). Toolbar, chips, and footer stay flat on the chrome. */
export function TCard({
    children,
    settle,
}: {
    children: React.ReactNode;
    /** stagger rows in on mount; key the card on filters to replay */
    settle?: boolean;
}) {
    return <div className={settle ? 'tcard settle' : 'tcard'}>{children}</div>;
}

/** Row-shaped placeholders while the first load is in flight. */
export function TSkeleton({ rows = 5 }: { rows?: number }) {
    return (
        <div aria-busy="true" aria-label="Loading">
            {Array.from({ length: rows }, (_, i) => (
                <div className="trow tskel" key={i}>
                    <span className="main">
                        <span
                            className="bar"
                            style={{ width: `${38 + ((i * 17) % 30)}%` }}
                        />
                        <span className="bar sm" style={{ width: '18%' }} />
                    </span>
                    <span className="bar dtb" />
                </div>
            ))}
        </div>
    );
}

export function TFoot({ children }: { children: React.ReactNode }) {
    return <div className="tfoot">{children}</div>;
}

export function TEmpty({
    message,
    actionLabel,
    onAction,
}: {
    message: string;
    actionLabel?: string;
    onAction?: () => void;
}) {
    return (
        <div className="tempty">
            <p className="e1">{message}</p>
            {actionLabel && (
                <button className="tb-btn" onClick={onAction}>
                    {actionLabel}
                </button>
            )}
        </div>
    );
}
